// Точка входа для страниц. Отдаёт window.SoundDetector, чтобы обычный
// (не-модульный) inline-скрипт тула мог этим пользоваться.
//
// Подключать ТОЛЬКО через @moduleGraph('js/sound-detector/index.js'):
// у модуля относительные импорты, а версия в query-строке ими не наследуется —
// под-модули залипли бы в кэше на полгода.

import { SoundModel } from './model.js';
import { LiveDetector } from './live.js';
import { PersonalTrainer } from './personal.js';
import { featuresFromAudio, featureVector, melFrames, loudestWindow, RATE, WINDOW_FRAMES } from './dsp.js';

// ⚠️ Свой адрес НЕЛЬЗЯ брать из import.meta.url. Деплойный esbuild зовётся без
// --format=esm, и в этом режиме он заменяет `import.meta` на пустой объект:
// в js-min получается `const E={}; new URL('./capture-worklet.js', E.url)` →
// «Failed to construct 'URL': Invalid URL», и модуль не поднимается ВООБЩЕ.
// Локально это невидимо, потому что локально отдаётся неминифицированный js/.
// (Файлы с top-level `await import()` уезжают в отдельный es2022-проход и там
// import.meta уцелевает — поэтому соседние модули работают, а этот падал.)
// Сторож: tests/Unit/ImportMetaSurvivesMinifyTest.php
function workletUrl(explicit) {
    if (explicit) return explicit;
    const tag = document.querySelector('script[type="module"][src*="/sound-detector/"]');
    if (tag && tag.src) return new URL('./capture-worklet.js', tag.src).href;
    return '/js/sound-detector/capture-worklet.js';
}

const modelCache = new Map();
async function loadModel(url) {
    let p = modelCache.get(url);
    if (!p) {
        p = fetch(url, { credentials: 'same-origin' })
            .then((r) => { if (!r.ok) throw new Error('модель не загрузилась: ' + r.status); return r.json(); })
            .then((j) => ({ model: SoundModel.fromJSON(j), meta: j }));
        modelCache.set(url, p);
    }
    return p;
}

/**
 * Живое распознавание на уже существующем графе WebAudio.
 *
 * @param {object} o
 * @param {AudioContext}  o.context     контекст страницы (свой не создаём — их лимит)
 * @param {AudioNode}     o.source      источник, обычно MediaStreamSource микрофона
 * @param {string}        o.weightsUrl  адрес весов
 * @param {string}        [o.label]     какой класс ловим (по умолчанию первый не-«other»)
 * @param {string}        [o.sensitivity] 'low' | 'medium' | 'high'
 * @param {function}      [o.onDetect]  сработало
 * @param {function}      [o.onScore]   каждая переоценка — для живого индикатора
 * @returns {Promise<object>} ручка: setSensitivity, setModel, mute, destroy
 */
export async function attach(o) {
    // Без weightsUrl детектор слушает «вхолостую»: кадры копятся, фон
    // собирается, обучение возможно — просто судить пока нечем. Это нужно
    // тулам, где цель задаёт сам пользователь и готовой модели нет вовсе.
    const loaded = o.weightsUrl ? await loadModel(o.weightsUrl) : { model: null, meta: {} };
    const model = loaded.model;
    const meta = loaded.meta;
    const rt = meta.runtime || {};
    const label = o.label || (model ? (model.labels.find((l) => l !== 'other') || model.labels[0]) : 'target');
    const level = o.sensitivity || rt.thresholdLevel || 'medium';

    const det = new LiveDetector({
        model, label,
        sampleRate: o.context.sampleRate,
        threshold: (meta.thresholds || {})[level] ?? 0,
        evalEvery: rt.evalEvery ?? 25,
        history: rt.history ?? 4,
        needed: rt.needed ?? 2,
        cooldownMs: rt.cooldownMs ?? 15000,
    });
    det.onDetect = o.onDetect || null;
    det.onScore = o.onScore || null;

    let node = null;
    let fallback = null;

    if (o.context.audioWorklet) {
        try {
            await o.context.audioWorklet.addModule(workletUrl(o.workletUrl));
            node = new AudioWorkletNode(o.context, 'sound-capture', { numberOfOutputs: 0 });
            node.port.onmessage = (e) => det.push(e.data);
            o.source.connect(node);
        } catch (err) {
            node = null;
            if (window.console) console.warn('[sound-detector] AudioWorklet недоступен, откат на ScriptProcessor', err);
        }
    }
    if (!node) {
        // Откат для старых движков. Работает на главном потоке, поэтому в
        // фоновой вкладке может подтормаживать — но лучше, чем ничего.
        fallback = o.context.createScriptProcessor(4096, 1, 1);
        fallback.onaudioprocess = (e) => det.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        o.source.connect(fallback);
        // ScriptProcessor не тикает, пока не подключён к выходу; глушим нулём.
        const mute = o.context.createGain();
        mute.gain.value = 0;
        fallback.connect(mute);
        mute.connect(o.context.destination);
        fallback._mute = mute;
    }

    return {
        detector: det,
        labels: model ? model.labels : [],
        hasModel: !!model,
        meta,
        usingWorklet: !!node,
        setSensitivity(lv) {
            const t = (meta.thresholds || {})[lv];
            if (typeof t === 'number') det.setThreshold(t);
        },
        setThreshold(t) { det.setThreshold(t); },
        /** Подменить модель на обученную пользователем, не пересобирая граф. */
        setModel(m, opts = {}) {
            det.model = m;
            if (opts.label) det.label = opts.label;
            if (typeof opts.threshold === 'number') det.setThreshold(opts.threshold);
            det.reset();
        },
        mute(v) {
            if (node) node.port.postMessage({ type: 'mute', value: !!v });
            else if (fallback) fallback.onaudioprocess = v ? null : (e) => det.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        },
        destroy() {
            try { o.source.disconnect(node || fallback); } catch (e) { /* граф мог быть уже разобран */ }
            if (node) { node.port.onmessage = null; try { node.disconnect(); } catch (e) {} }
            if (fallback) {
                fallback.onaudioprocess = null;
                try { fallback.disconnect(); fallback._mute.disconnect(); } catch (e) {}
            }
        },
    };
}

/** Признаки из готового куска звука — для обучения на записанных примерах. */
export function featuresFrom(samples, sampleRate) {
    return featuresFromAudio(samples, sampleRate);
}

/**
 * Признаки со ВСЕХ окон куска — так обучается модель, которая потом работает
 * на потоке. Брать только самое громкое окно НЕЛЬЗЯ: живой детектор видит то
 * окно, какое пришло, и обучение на «лучших» окнах даёт train/serve mismatch
 * (поймано сквозным тестом, полнота по эпизодам падала с 88% до 76%).
 */
export function featuresFromAll(samples, sampleRate, stride = 25) {
    const frames = melFrames(samples, sampleRate);
    const out = [];
    for (let s = 0; s + WINDOW_FRAMES <= frames.length; s += stride) {
        out.push(featureVector(frames.slice(s, s + WINDOW_FRAMES)));
    }
    if (!out.length && frames.length) out.push(featureVector(loudestWindow(frames)));
    return out;
}

export { SoundModel, LiveDetector, PersonalTrainer, RATE };

window.SoundDetector = {
    attach, featuresFrom, featuresFromAll,
    SoundModel, LiveDetector, PersonalTrainer, RATE,
};
window.dispatchEvent(new CustomEvent('sound-detector:ready'));
